import { Button, Modal, Select, Skeleton, UserInfo, useToast } from '@/components';
import { For, Show, createEffect, createSignal } from 'solid-js';
import { usePollViewerCandidates, usePollViewers, useReplacePollViewers } from './hooks';

export const ViewersModal = (props: { open: boolean; pollId: string; onClose: () => void }) => {
  const viewers = usePollViewers(() => props.pollId, props.open);
  const candidates = usePollViewerCandidates(() => props.pollId, props.open);
  const save = useReplacePollViewers(props.pollId);
  const toast = useToast();
  // Ordered list of chosen user ids; newest additions go to the top.
  const [selected, setSelected] = createSignal<string[]>([]);

  createEffect(() => {
    if (viewers.data) setSelected(viewers.data.data.map((user) => user.id));
  });

  const users = () => candidates.data?.data ?? [];
  // Saved viewers may no longer be candidates (e.g. disabled) — still list them so they can be removed.
  const known = () => [...(viewers.data?.data ?? []), ...users()];
  const chosen = () => selected().flatMap((id) => known().find((user) => user.id === id) ?? []);
  const options = () =>
    users()
      .filter((user) => !selected().includes(user.id))
      .map((user) => ({
        value: user.id,
        label: user.displayName ? `${user.displayName} (${user.email})` : user.email,
      }));

  let list: HTMLUListElement | undefined;
  let picker: HTMLDivElement | undefined;
  // Keep keyboard focus in the dialog: next row's Remove, else the add-viewer control.
  const remove = (id: string, index: number) => {
    setSelected((current) => current.filter((item) => item !== id));
    queueMicrotask(() => {
      const buttons = list?.querySelectorAll('button') ?? [];
      (buttons[index] ?? buttons[index - 1] ?? picker?.querySelector('button'))?.focus();
    });
  };

  const submit = async () => {
    await save.mutateAsync(selected());
    toast.success('Dashboard viewers updated');
    props.onClose();
  };

  return (
    <Modal open={props.open} onClose={props.onClose} title="Dashboard viewers" size="md">
      <Show when={!candidates.isPending && !viewers.isPending} fallback={<Skeleton class="h-32" />}>
        <div class="flex flex-col gap-4">
          <div ref={picker}>
            <Select
              label="Add a viewer"
              placeholder={options().length ? 'Search people…' : 'Everyone is already added'}
              options={options()}
              value=""
              onChange={(id) => id && setSelected((current) => [id, ...current])}
            />
          </div>
          <div>
            <p class="mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Can view results ({chosen().length})
            </p>
            <Show
              when={chosen().length}
              fallback={
                <p class="rounded-lg border border-dashed border-neutral-300 px-4 py-6 text-center text-sm text-neutral-500 dark:border-neutral-700">
                  No viewers yet — only you can see the results.
                </p>
              }
            >
              <ul
                ref={list}
                class="flex max-h-72 flex-col divide-y divide-neutral-200 overflow-y-auto rounded-lg border border-neutral-200 dark:divide-neutral-800 dark:border-neutral-800"
              >
                <For each={chosen()}>
                  {(user, index) => (
                    <li class="flex items-center justify-between gap-3 px-3 py-2">
                      <UserInfo user={user} />
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Remove ${user.displayName || user.email}`}
                        onClick={() => remove(user.id, index())}
                      >
                        Remove
                      </Button>
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          </div>
        </div>
      </Show>
      <div class="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={props.onClose}>
          Cancel
        </Button>
        <Button loading={save.isPending} onClick={submit}>
          Save viewers
        </Button>
      </div>
    </Modal>
  );
};
